import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@repo/api-client";
import type { Course } from "@repo/shared-types";

const CATEGORIES = ["All", "meditation", "dream-science", "philosophy", "ayurveda", "healing", "bhakti"];
const ACCESS_LABELS: Record<string, string> = { free: "Free", member: "Member", paid: "Paid" };
const ACCESS_PILL: Record<string, string> = {
  free: "cap-pill cap-pill--green",
  member: "cap-pill cap-pill--indigo",
  paid: "cap-pill cap-pill--gold",
};

export default function CoursesPage() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [accessFilter, setAccessFilter] = useState("all");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { courses: data } = await api.listCourses({
        category: category !== "All" ? category : undefined,
        access: accessFilter !== "all" ? accessFilter : undefined,
      });
      setCourses(data);
      setLoading(false);
    })();
  }, [category, accessFilter]);

  return (
    <div className="cap-page">
      <section className="cap-hero">
        <div className="container">
          <div className="cap-hero__shell cap-hero__shell--split">
            <div>
              <span className="cap-hero__badge">Enlightenment Through Learning</span>
              <h1 className="display-1 cap-hero__title">Courses for the path inward</h1>
              <p className="cap-hero__subtitle">
                Structured journeys through meditation, dream science, Ayurveda, and devotional practice.
                Designed to feel calm, premium, and easy to continue.
              </p>
              <div className="hero-actions">
                <Link to="/teachers" className="btn btn--gold">Meet the teachers</Link>
                <Link to="/stories" className="btn btn--outline">Read seeker stories</Link>
              </div>
            </div>

            <aside className="cap-hero__aside">
              <div className="cap-hero__card">
                <div className="cap-stat-grid">
                  <div className="cap-stat"><span className="cap-stat__value">6</span><span className="cap-stat__label">seed courses</span></div>
                  <div className="cap-stat"><span className="cap-stat__value">3</span><span className="cap-stat__label">teachers</span></div>
                  <div className="cap-stat"><span className="cap-stat__value">12</span><span className="cap-stat__label">lessons on meditation</span></div>
                  <div className="cap-stat"><span className="cap-stat__value">3</span><span className="cap-stat__label">access tiers</span></div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="section section--surface-alt">
        <div className="container">
          <div className="cap-toolbar">
            <div className="cap-filter-row" role="tablist" aria-label="Course categories">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`cap-chip ${category === c ? "cap-chip--active" : ""}`}
                >
                  {c.replace("-", " ")}
                </button>
              ))}
            </div>
            <select
              value={accessFilter}
              onChange={(e) => setAccessFilter(e.target.value)}
              className="cap-select"
            >
              <option value="all">All access</option>
              <option value="free">Free</option>
              <option value="member">Member</option>
              <option value="paid">Paid</option>
            </select>
          </div>

          {loading ? (
            <p className="text-muted body-lg text-center" style={{ padding: "4rem 0" }}>Loading courses…</p>
          ) : courses.length === 0 ? (
            <p className="text-muted body-lg text-center" style={{ padding: "4rem 0" }}>No courses found.</p>
          ) : (
            <div className="cap-grid cap-grid--courses">
              {courses.map((course) => (
                <CourseCard key={course.id} course={course} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function CourseCard({ course }: { course: Course }) {
  const stars = course.rating ? Math.round(course.rating) : null;
  return (
    <Link
      to={`/courses/${course.slug}`}
      className="cap-card"
    >
      <div className="cap-card__media">
        {course.image_url ? <img src={course.image_url} alt={course.title} /> : <span aria-hidden="true">🙏</span>}
      </div>
      <div className="cap-card__body">
        <div className="cap-card__meta-row">
          <span className={ACCESS_PILL[course.access]}>
            {ACCESS_LABELS[course.access]}{course.access === "paid" && course.price ? ` · $${course.price}` : ""}
          </span>
          <span className="cap-pill" style={{ background: "rgba(61, 43, 31, 0.06)", color: "var(--c-muted)" }}>{course.level}</span>
        </div>
        <h3 className="heading-lg">{course.title}</h3>
        {course.subtitle && <p className="text-muted body-sm">{course.subtitle}</p>}
        <p className="body-sm text-muted">By {course.teacher_name}</p>
        <p className="body-sm" style={{ color: "var(--c-ink-75)" }}>{course.description}</p>
        <div className="cap-card__meta-row" style={{ marginTop: "auto", fontSize: "0.82rem", color: "var(--c-muted)" }}>
          <span>📚 {course.lesson_count} lessons</span>
          <span>⏱ {Math.round(course.duration_total_minutes / 60)}h</span>
          {stars && <span>★ {course.rating?.toFixed(1)}</span>}
          <span style={{ marginLeft: "auto" }}>{course.enrolled_count.toLocaleString()} enrolled</span>
        </div>
      </div>
    </Link>
  );
}
